import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCopy, FiInstagram, FiX, FiCheck } = FiIcons;

export default function OrderModal({ onClose }) {
  const [copied, setCopied] = useState(false);
  const orderMessage = "I want to buy Mee Matcha";

  const handleCopy = () => {
    navigator.clipboard.writeText(orderMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenInstagram = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Attempt deep link on mobile
      window.location.href = "instagram://user?username=mee.matchaclub";
      
      // Fallback to web URL if the app doesn't open
      setTimeout(() => {
        if (!document.hidden) {
          window.open("https://www.instagram.com/mee.matchaclub/", "_blank");
        }
      }, 800);
    } else {
      // Direct web URL for desktop
      window.open("https://www.instagram.com/mee.matchaclub/", "_blank");
    }
    
    // Smoothly close modal after trigger
    setTimeout(onClose, 500);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 overflow-hidden">
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
      />

      {/* Modal Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative w-full max-w-md bg-brand-green/80 border border-brand-cream/10 backdrop-blur-2xl rounded-[2.5rem] p-8 sm:p-10 shadow-2xl overflow-hidden"
      >
        {/* Subtle decorative gold line at top */}
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-brand-gold/30 to-transparent" />

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-brand-cream/40 hover:text-brand-cream transition-colors"
        >
          <SafeIcon icon={FiX} className="text-2xl" />
        </button>

        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-cream mb-4">
            Ready to Order Your Matcha?
          </h2>
          <p className="text-brand-cream/70 text-sm sm:text-base mb-8 leading-relaxed">
            Click below and send us this message on Instagram to start your order:
          </p>

          {/* Message Block */}
          <div className="relative group mb-8">
            <div className="absolute inset-0 bg-brand-gold/5 blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative bg-black/30 border border-brand-gold/20 rounded-2xl p-5 flex flex-col items-center justify-center">
              <span className="text-brand-gold font-serif italic text-lg sm:text-xl">
                “{orderMessage}”
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4">
            <motion.button
              onClick={handleCopy}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative py-4 rounded-2xl font-bold tracking-wide transition-all duration-300 flex items-center justify-center gap-3 border ${
                copied 
                ? 'bg-brand-green text-brand-gold border-brand-gold' 
                : 'bg-brand-cream/5 text-brand-cream border-brand-cream/20 hover:bg-brand-cream/10'
              }`}
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              {copied ? 'Message Copied ✓' : 'Copy Message'}
              
              {copied && (
                <motion.div 
                  layoutId="glow"
                  className="absolute inset-0 rounded-2xl shadow-[0_0_20px_rgba(201,162,39,0.3)]"
                />
              )}
            </motion.button>

            <motion.button
              onClick={handleOpenInstagram}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="py-4 bg-brand-cream text-brand-green rounded-2xl font-bold tracking-wide shadow-lg shadow-brand-red/10 flex items-center justify-center gap-3"
            >
              <SafeIcon icon={FiInstagram} className="text-xl" />
              Open Instagram
            </motion.button>
          </div>
        </div>

        {/* Small Toast Notification */}
        <AnimatePresence>
          {copied && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-brand-gold text-brand-green text-xs font-bold rounded-full shadow-lg"
            >
              Copied to clipboard
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}